import { useState, type ReactNode } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

interface FigmaStepModalProps {
  embedded?: boolean;
  stepNumber: number;
  totalSteps: number;
  title: string;
  description: ReactNode;
  error?: string | null;
  theme?: 'default' | 'success';
  progressMode?: 'active' | 'complete';
  progressLabel?: string;
  stepTitle?: string;
  stepLabels?: string[];
  showBadge?: boolean;
  badgeLabel?: string;
  badgeIcon?: ReactNode;
  badgeTheme?: 'default' | 'success';
  headerAside?: ReactNode;
  footerPrimary?: ReactNode;
  footerSecondary?: ReactNode;
  footerLayout?: 'split' | 'stacked-center';
  maxWidthClassName?: string;
  titleBelow?: boolean;
  children: ReactNode;
}

const FigmaStepModal: React.FC<FigmaStepModalProps> = ({
  embedded = false,
  stepNumber,
  totalSteps,
  title,
  description,
  error,
  theme = 'default',
  progressMode = 'active',
  showBadge = true,
  badgeLabel = 'AI Priming Active',
  badgeIcon,
  badgeTheme,
  stepLabels,
  headerAside,
  footerPrimary,
  footerSecondary,
  footerLayout = 'split',
  maxWidthClassName = 'max-w-[768px]',
  titleBelow = false,
  children,
}) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('he') || i18n.language.startsWith('ar');
  const isSuccess = theme === 'success';
  const progressFillClassName = isSuccess
    ? 'bg-gradient-to-l from-success-500 to-success-700'
    : 'bg-gradient-to-l from-brand-400 to-brand-700';
  const accentTextClassName = isSuccess ? 'text-success-600 dark:text-success-400' : 'text-brand-700 dark:text-brand-300';
  const isBadgeSuccess = (badgeTheme ?? theme) === 'success';
  const badgeBorderClassName = isBadgeSuccess
    ? 'border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-900/20'
    : 'border-brand-200 bg-brand-50/80 dark:border-brand-800 dark:bg-brand-900/20';
  const badgeTextClassName = isBadgeSuccess ? 'text-success-700 dark:text-success-300' : 'text-brand-700 dark:text-brand-300';
  const [isScrolled, setIsScrolled] = useState(false);

  const handleContentScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setIsScrolled(e.currentTarget.scrollTop > 8);
  };


  const cardMotionProps = embedded
    ? {}
    : ({
        initial: { opacity: 0, scale: 0.96, y: 24 } as const,
        animate: { opacity: 1, scale: 1, y: 0 } as const,
        transition: { duration: 0.38, ease: [0.16, 1, 0.3, 1] as const },
      });

  const modalCard = (
    <motion.div
      {...cardMotionProps}
      dir={isRTL ? 'rtl' : 'ltr'}
      className={`relative z-10 flex max-h-[92vh] w-full flex-col overflow-hidden rounded-2xl border border-neutral-200/80 bg-white shadow-[0px_32px_64px_-12px_rgba(25,28,30,0.15)] dark:border-neutral-700 dark:bg-neutral-800 ${maxWidthClassName}`}
    >
      {/* Scrollable area — title sticky, everything else scrolls */}
      <div
        className="flex-1 overflow-y-auto"
        onScroll={handleContentScroll}
      >
        {/* Sticky title bar — hidden when titleBelow is true */}
        {!titleBelow && (
          <div
            className={`sticky top-0 z-10 bg-white px-10 pb-4 pt-10 transition-shadow duration-200 dark:bg-neutral-800 ${
              isScrolled ? 'shadow-sm border-b border-neutral-200 dark:border-neutral-700' : ''
            }`}
          >
            <motion.h2
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.32, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={`text-[30px] font-bold leading-9 tracking-[-0.75px] ${accentTextClassName}`}
            >
              {title}
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.32, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              className="mt-2 whitespace-pre-line text-base leading-6 text-text-700 dark:text-neutral-300"
            >
              {description}
            </motion.div>
          </div>
        )}

        {/* Scrollable header content: progress bar + badge + description + headerAside */}
        <div className={`px-10 pb-5 ${titleBelow ? 'pt-10' : 'pt-4'}`}>
          {/* Animated progress bar */}
          <div className="mb-6 w-full">
            {/* due to styling issue the rtl is inverted, so we need to invert the direction */}
            <div className="flex items-end gap-3" dir={isRTL ? 'ltr' : 'rtl'}>
              {Array.from({ length: totalSteps }, (_, index) => {
                const visualStep = index + 1;
                const isActive = visualStep === stepNumber;
                const isFilled = progressMode === 'complete' || visualStep <= stepNumber;
                const isLabelVisible = progressMode === 'complete' || visualStep <= stepNumber;
                const isCurrentStep = visualStep === stepNumber && progressMode !== 'complete';

                return (
                  <div key={visualStep} className="flex flex-1 flex-col gap-1.5">
                    {stepLabels ? (
                      <span
                        className={`block truncate text-[11px] font-semibold leading-none transition-colors duration-300 ${
                          isLabelVisible
                            ? isCurrentStep
                              ? accentTextClassName
                              : 'text-neutral-400 dark:text-neutral-500'
                            : 'invisible'
                        }`}
                      >
                        {stepLabels[index] ?? ''}
                      </span>
                    ) : null}
                    <div className="relative h-2 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
                      <motion.div
                        className={`absolute inset-0 rounded-full ${isFilled ? progressFillClassName : ''}`}
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: isFilled ? 1 : 0 }}
                        style={{ transformOrigin: isRTL ? 'right center' : 'left center' }}
                        transition={{
                          duration: 0.55,
                          delay: isFilled ? index * 0.08 : 0,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                      />
                      {isActive && progressMode !== 'complete' && (
                        <motion.div
                          className="absolute inset-0 rounded-full bg-white/30"
                          animate={{ x: ['200%', '-100%'] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.8 }}
                          style={{ width: '40%' }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Title below progress bars — shown when titleBelow is true */}
          {titleBelow && (
            <>
              <motion.h2
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.32, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className={`mb-2 text-[30px] font-bold leading-9 tracking-[-0.75px] ${accentTextClassName}`}
              >
                {title}
              </motion.h2>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.32, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
                className="mb-4 whitespace-pre-line text-base leading-6 text-text-700 dark:text-neutral-300"
              >
                {description}
              </motion.div>
            </>
          )}

          {/* Badge row */}
          {showBadge ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.85, x: isRTL ? 8 : -8 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.32, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className={`shrink-0 rounded-full border px-[17px] py-[9px] ${badgeBorderClassName}`}
            >
              <div className={`flex items-center gap-2 text-[12px] font-bold ${badgeTextClassName}`}>
                <span>{badgeLabel}</span>
                {badgeIcon ?? <SparklesIcon className="h-3.5 w-3.5" />}
              </div>
            </motion.div>
          ) : null}

          {headerAside ? <div className="mt-4">{headerAside}</div> : null}
        </div>

        {/* Children */}
        <div className="px-10 pb-8 pt-2">
          {error ? (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="mb-6 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-right text-sm text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300"
            >
              {error}
            </motion.div>
          ) : null}
          {children}
        </div>
      </div>

      {/* Footer */}
      {(footerPrimary || footerSecondary) ? (
        <div
          className={`border-t border-neutral-200 bg-neutral-50/80 px-10 pb-6 pt-5 dark:border-neutral-700 dark:bg-neutral-900/80 ${
            footerLayout === 'stacked-center'
              ? 'flex flex-col items-center gap-5'
              : 'flex items-center justify-between'
          }`}
        >
            <div className={footerLayout === 'stacked-center' ? 'w-full max-w-[384px]' : ''}>{footerPrimary}</div>
          <div>{footerSecondary}</div>
        
        </div>
      ) : null}
    </motion.div>
  );

  if (embedded) {
    return modalCard;
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.22 }}
        className="absolute inset-0 bg-neutral-950/60 backdrop-blur-[14px] dark:bg-neutral-950/70"
      />
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-12 top-12 h-96 w-96 rounded-full bg-brand-500/10 blur-[60px] dark:bg-brand-700/20" />
        <div className="absolute bottom-12 right-12 h-[480px] w-[480px] rounded-full bg-success-500/5 blur-[75px] dark:bg-success-700/10" />
      </div>
      {modalCard}
    </div>
  );
};

export default FigmaStepModal;
